import {
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  CircularProgress,
  Chip,
  Box,
  TextField,
  InputAdornment,
  LinearProgress,
  Collapse,
  Pagination,
  PaginationItem,
  Select as MuiSelect,
  Checkbox as MuiCheckbox,
  ListItemText,
  ListSubheader,
} from "@mui/material";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { useWindowDimensions } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  ClearActionLog,
  ClearCurrentAction,
  ClearFullAnalyseString,
  SetControllerToggle,
} from "../redux/actions/controlAction";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import DashboardStatsBox from "../components/DashboardStatsBox";
import {
  CloseOutlined,
  FilterListOutlined,
  SearchOutlined,
  SportsScore,
  TrendingUp,
  HourglassBottom,
  PieChartOutline,
  AccessTime,
  ArrowBack,
  ArrowForward,
  SportsSoccer,
  KeyboardArrowDown,
  PlayArrowRounded,
  InfoOutlined,
  CellTowerRounded,
  FilterList,
  History,
  Refresh,
} from "@mui/icons-material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import moment from "moment/moment";
import UpdateReviewStatusModal from "../components/UpdateReviewStatusModal";
import ActionConfirmationModal from "../components/ActionConfirmationModal";
import { useUpdateMatchStatus } from "../hooks/useUpdateMatchStatus";
import { trackEvent } from "../utils/mixpanel";

const CustomDateInput = forwardRef(({ value, onClick, className }, ref) => (
  <Button
    className={className}
    onClick={onClick}
    ref={ref}
    sx={{
      height: "38px",
      fontSize: "13px",
      border: "1px solid var(--border-1)",
      borderRadius: "8px",
      color: value ? "var(--text-1)" : "var(--text-2)",
      backgroundColor: "var(--surface-1)",
      textTransform: "none",
      fontWeight: "400",
      px: 2,
      justifyContent: "flex-start",
      "&:hover": {
        borderColor: "var(--text-2)",
        backgroundColor: "var(--surface-1)",
      },
    }}
  >
    {value || "Select date"}
  </Button>
));

const FilterSelectField = ({
  options,
  value,
  onChange,
  placeholder = "Select",
  searchable = false,
  labelKey = "label",
  valueKey = "value",
}) => {
  const [searchText, setSearchText] = useState("");
  const filtered = searchable
    ? options.filter((o) =>
        (o[labelKey] || "").toLowerCase().includes(searchText.toLowerCase()),
      )
    : options;
  return (
    <MuiSelect
      multiple
      displayEmpty
      value={value}
      onChange={(e) => onChange(e.target.value)}
      renderValue={(selected) => {
        if (selected.length === 0)
          return (
            <span style={{ color: "var(--text-2)", fontSize: "12px" }}>
              {placeholder}
            </span>
          );
        if (selected.length === 1) {
          const item = options.find((o) => o[valueKey] === selected[0]);
          return (
            <span style={{ fontSize: "12px", color: "var(--text-1)" }}>
              {item?.[labelKey] ?? selected[0]}
            </span>
          );
        }
        return (
          <span
            style={{
              fontSize: "12px",
              color: "var(--text-1)",
              fontWeight: 500,
            }}
          >
            Selected ({selected.length})
          </span>
        );
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            backgroundColor: "var(--surface-1)",
            border: "1px solid var(--border-1)",
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            maxHeight: 260,
          },
        },
        disableAutoFocusItem: true,
      }}
      sx={{
        height: "38px",
        fontSize: "12px",
        borderRadius: "8px",
        backgroundColor: "var(--surface-2)",
        color: "var(--text-1)",
        width: "100%",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "var(--border-1)",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "var(--text-2)",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(232,108,0,0.6)",
        },
        "& .MuiSelect-icon": { color: "var(--text-2)", fontSize: "18px" },
      }}
    >
      {searchable && (
        <ListSubheader
          sx={{
            backgroundColor: "var(--surface-1)",
            px: 1,
            pt: 1,
            pb: 0.5,
            lineHeight: "normal",
          }}
        >
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Search..."
            value={searchText}
            onChange={(e) => {
              e.stopPropagation();
              setSearchText(e.target.value);
            }}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined
                    sx={{ fontSize: 15, color: "var(--text-2)" }}
                  />
                </InputAdornment>
              ),
              sx: {
                fontSize: "12px",
                borderRadius: "8px",
                height: "32px",
                backgroundColor: "var(--surface-2)",
                "& fieldset": { borderColor: "var(--border-1)" },
              },
            }}
          />
        </ListSubheader>
      )}
      {filtered.length === 0 && (
        <MenuItem disabled sx={{ fontSize: "12px", color: "var(--text-2)" }}>
          No results
        </MenuItem>
      )}
      {filtered.map((option) => (
        <MenuItem
          key={option[valueKey]}
          value={option[valueKey]}
          sx={{ fontSize: "12px", py: 0.5, color: "var(--text-1)", gap: 0.5 }}
        >
          <MuiCheckbox
            checked={value.includes(option[valueKey])}
            size="small"
            sx={{
              p: 0.5,
              color: "var(--text-2)",
              "&.Mui-checked": { color: "rgba(232,108,0,1)" },
            }}
          />
          <ListItemText
            primary={option[labelKey]}
            primaryTypographyProps={{
              fontSize: "12px",
              color: "var(--text-1)",
            }}
          />
        </MenuItem>
      ))}
    </MuiSelect>
  );
};

const StatsCard = ({
  icon,
  value,
  label,
  subtitle,
  accent,
  valueComponent,
}) => (
  <Stack
    sx={{
      flex: "1 1 200px",
      maxWidth: { xs: "40%", md: "24%" },
      p: { xs: 1.5, md: 2 },
      borderRadius: "12px",
      backgroundColor: "var(--surface-1)",
      border: "1px solid var(--border-1)",
      gap: 1,
      transition: "box-shadow 0.2s, border-color 0.2s",
      "&:hover": {
        boxShadow: "var(--shadow-1)",
        borderColor: accent || "var(--border-1)",
      },
    }}
  >
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: accent ? `${accent}15` : "var(--surface-2)",
          color: accent || "var(--text-2)",
        }}
      >
        {icon}
      </Box>
      {valueComponent ? (
        <Box sx={{ width: 44, height: 44 }}>{valueComponent}</Box>
      ) : (
        <Typography
          sx={{
            fontSize: { xs: "24px", md: "28px" },
            fontWeight: "700",
            lineHeight: 1,
            color: "var(--text-1)",
          }}
        >
          {value ?? "--"}
        </Typography>
      )}
    </Stack>
    <Stack>
      <Typography
        sx={{
          fontSize: { xs: "12px", md: "13px" },
          fontWeight: "600",
          color: "var(--text-1)",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: "10px", md: "11px" },
          color: "var(--text-2)",
          lineHeight: 1.3,
        }}
      >
        {subtitle}
      </Typography>
    </Stack>
  </Stack>
);

const StatusChip = ({ status }) => {
  const config = getStatusConfig(status);
  return (
    <Chip
      label={config.text}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "11px",
        height: "24px",
        backgroundColor: `${config.color}18`,
        color: config.color,
        border: `1px solid ${config.color}40`,
        "& .MuiChip-label": { px: 1.5 },
      }}
    />
  );
};

function getStatusConfig(status) {
  switch (status) {
    case 0:
      return { text: "Inactive", color: "#6B7280" };
    case 1:
      return { text: "Created", color: "#6B7280" };
    case 2:
      return { text: "Video Uploaded", color: "#3B82F6" };
    case 3:
      return { text: "Analysis Done", color: "#10B981" };
    case 4:
      return { text: "Assigned", color: "#6366F1" };
    case 5:
      return { text: "In Progress", color: "#F59E0B" };
    case 6:
      return { text: "Review In Progress", color: "#8B5CF6" };
    case 7:
      return { text: "Completed", color: "#059669" };
    case 8:
      return { text: "Marked for Review", color: "#EC4899" };
    case 9:
      return { text: "Issue Found", color: "#EF4444" };
    default:
      return { text: "Unknown", color: "#6B7280" };
  }
}

const analysisOptions = [
  { value: 1, label: "Match Created" },
  { value: 2, label: "Video Uploaded" },

  { value: 4, label: "Resources Assigned" },
  { value: 5, label: "Analysis In Progress" },
  { value: 3, label: "Analysis Done" },

  { value: 6, label: "Review In Progress" },

  { value: 8, label: "Mark For Review" },
  { value: 9, label: "Issue Found" },
  { value: 7, label: "Completed" },
];

const formatTimeFromSecondsToMinutes = (seconds) => {
  if (!seconds) return "N/A";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

const formatTimeFromSeconds = (seconds) => {
  if (!seconds) return "--";
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (hrs > 0) return `${hrs}h ${mins}m`;
  return `${mins}m`;
};

const Home = (props) => {
  function useQueryHook() {
    return new URLSearchParams(useLocation().search);
  }
  const matchList = props.matchList;
  useQueryHook();
  const dispatch = useDispatch();

  const { width } = useWindowDimensions();
  const isMobile = width < 850;
  const [searchMatchId, setSearchMatchId] = useState("");
  const [searchTournament, setSearchTournament] = useState("");
  const [searchTeam, setSearchTeam] = useState("");
  const [searchByAnalyzed, setSearchByAnalyzed] = useState();
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [mainListData, setMainListData] = useState([]);
  const [filterShown, setFilterShown] = useState(false);
  const [selectAnalyzedArray, setSelectAnalyzedArray] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const [isDarkMode, setIsDarkMode] = useState(
    document.body.classList.contains("theme-dark"),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains("theme-dark"));
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    dispatch(ClearActionLog());
    dispatch(ClearCurrentAction());
    dispatch(ClearFullAnalyseString());
  }, [dispatch]);

  useEffect(() => {
    setMainListData(props.matchList?.match_data?.data || []);
  }, [props.matchList]);

  const totalPages = Math.ceil(props?.count / 20);
  const overview = matchList?.analyst_overview;
  const progressPercent = overview?.total_matches_assigned
    ? parseInt(
        (overview.analysed_matches / overview.total_matches_assigned) * 100,
      )
    : 0;

  const handleSearch = () => {
    setCurrentPage(1);
    if (props?.onPress) {
      props.onPress({
        page: 0,
        searchMatchId,
        searchTournament,
        searchTeam,
        searchByAnalyzed: selectAnalyzedArray,
        startDate: startDate
          ? new Intl.DateTimeFormat("fr-CA", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            }).format(startDate)
          : "",
        endDate: endDate
          ? new Intl.DateTimeFormat("fr-CA", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            }).format(endDate)
          : "",
      });
    }
  };

  const handleReset = () => {
    setSearchMatchId("");
    setSearchTournament("");
    setSearchTeam("");
    setStartDate("");
    setEndDate("");
    setSelectAnalyzedArray([]);
    if (props?.onPress) {
      props.onPress({
        page: 0,
        searchMatchId: "",
        searchTournament: "",
        searchTeam: "",
        searchByAnalyzed: [],
        startDate: "",
        endDate: "",
      });
    }
  };

  const handlePageChange = (_, value) => {
    setCurrentPage(value);
    props.setPage && props.setPage((parseInt(value) - 1) * 20);
  };

  const hasActiveFilters =
    searchMatchId ||
    searchTournament ||
    searchTeam ||
    selectAnalyzedArray.length > 0 ||
    startDate ||
    endDate;

  return (
    <Stack spacing={2.5} sx={{ width: "100%" }}>
      {/* Stats Overview */}
      <Stack
        direction="row"
        sx={{
          gap: { xs: 1, md: 1.5 },
          flexWrap: "wrap",
        }}
      >
        <StatsCard
          icon={<SportsScore fontSize="small" />}
          value={overview?.total_matches_assigned}
          label="Total Assigned"
          subtitle="Matches assigned to you"
          accent="#3B82F6"
        />
        <StatsCard
          icon={<TrendingUp fontSize="small" />}
          value={overview?.analysed_matches}
          label="Completed"
          subtitle="Analysis completed"
          accent="#10B981"
        />
        <StatsCard
          icon={<HourglassBottom fontSize="small" />}
          value={overview?.in_progress_matches}
          label="In Progress"
          subtitle="Currently being analyzed"
          accent="#F59E0B"
        />
        <StatsCard
          icon={<PieChartOutline fontSize="small" />}
          label="Overall Progress"
          accent="#8B5CF6"
          subtitle={
            <Stack direction="row" alignItems="center" gap={0.5}>
              <AccessTime sx={{ fontSize: 11 }} />
              <span>
                Avg TAT:{" "}
                <span style={{ fontWeight: 600, color: "#10B981" }}>
                  {formatTimeFromSecondsToMinutes(overview?.average_tat)}
                </span>
              </span>
            </Stack>
          }
          valueComponent={
            <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
              <CircularProgressbar
                value={progressPercent}
                strokeWidth={10}
                styles={buildStyles({
                  rotation: 0.25,
                  strokeLinecap: "round",
                  pathTransitionDuration: 0.5,
                  pathColor: "#8B5CF6",
                  trailColor: isDarkMode
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.08)",
                })}
              />
              <Typography
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  fontSize: "11px",
                  fontWeight: "700",
                  color: "var(--text-1)",
                }}
              >
                {progressPercent}%
              </Typography>
            </Box>
          }
        />
      </Stack>

      {/* Toolbar: Filter Toggle + Search + Pagination Summary */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          backgroundColor: "var(--surface-1)",
          border: "1px solid var(--border-1)",
          borderRadius: "12px",
          px: 2,
          py: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Tooltip title={filterShown ? "Hide filters" : "Show filters"}>
            <IconButton
              onClick={() => setFilterShown(!filterShown)}
              size="small"
              sx={{
                backgroundColor: filterShown
                  ? "var(--primary-1)"
                  : hasActiveFilters
                    ? "rgba(232, 108, 0, 0.12)"
                    : "var(--surface-2)",
                color: filterShown
                  ? "#fff"
                  : hasActiveFilters
                    ? "rgba(232, 108, 0, 1)"
                    : "var(--text-2)",
                borderRadius: "8px",
                width: 36,
                height: 36,
                "&:hover": {
                  backgroundColor: filterShown
                    ? "var(--primary-1)"
                    : "var(--surface-2)",
                },
              }}
            >
              <FilterListOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "500",
              color: "var(--text-1)",
              display: { xs: "none", md: "block" },
            }}
          >
            Matches
          </Typography>
          {hasActiveFilters && (
            <Chip
              label="Filters active"
              size="small"
              onDelete={handleReset}
              sx={{
                fontSize: "11px",
                height: "24px",
                backgroundColor: "rgba(232, 108, 0, 0.12)",
                color: "rgba(232, 108, 0, 1)",
                "& .MuiChip-deleteIcon": {
                  color: "rgba(232, 108, 0, 0.6)",
                  fontSize: "16px",
                },
              }}
            />
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          {props.isLoading && (
            <CircularProgress size={18} sx={{ color: "var(--text-2)" }} />
          )}
          <Typography
            sx={{
              fontSize: "13px",
              color: "var(--text-2)",
              display: { xs: "none", sm: "block" },
              mr: 0.5,
            }}
          >
            {props.count > 0
              ? `${(currentPage - 1) * 20 + 1}–${Math.min(currentPage * 20, props.count)} of ${props.count}`
              : "No matches"}
          </Typography>
          {props.onRefresh && (
            <Tooltip title="Refresh">
              <span>
                <IconButton
                  size="small"
                  disabled={props.isLoading}
                  onClick={() => props.onRefresh()}
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "6px",
                    border: "1px solid var(--border-1)",
                    color: "var(--text-2)",
                    "&.Mui-disabled": { opacity: 0.3 },
                  }}
                >
                  <Refresh sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {totalPages > 1 && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton
                size="small"
                disabled={currentPage <= 1}
                onClick={() => handlePageChange(null, currentPage - 1)}
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "6px",
                  border: "1px solid var(--border-1)",
                  color: "var(--text-2)",
                  "&.Mui-disabled": { opacity: 0.3 },
                }}
              >
                <ArrowBack sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                disabled={currentPage >= totalPages}
                onClick={() => handlePageChange(null, currentPage + 1)}
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "6px",
                  border: "1px solid var(--border-1)",
                  color: "var(--text-2)",
                  "&.Mui-disabled": { opacity: 0.3 },
                }}
              >
                <ArrowForward sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
          )}
        </Stack>
      </Stack>

      {/* Filter Panel */}
      <Collapse in={filterShown}>
        <Stack
          className="home-filter-panel"
          direction={{ xs: "column", md: "row" }}
          alignItems={{ md: "flex-end" }}
          spacing={1.5}
          sx={{
            backgroundColor: "var(--surface-1)",
            border: "1px solid var(--border-1)",
            borderRadius: "12px",
            p: 1.5,
          }}
        >
          {/* Match ID */}
          <Stack sx={{ flex: "1.2 1 0", minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                color: "var(--text-2)",
                mb: 0.5,
              }}
            >
              Match ID
            </Typography>
            <TextField
              size="small"
              placeholder="e.g. 1234"
              value={searchMatchId}
              onChange={(e) => setSearchMatchId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined
                      sx={{ fontSize: 16, color: "var(--text-2)" }}
                    />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: "8px",
                  fontSize: "12px",
                  height: "38px",
                  backgroundColor: "var(--surface-2)",
                  "& fieldset": { borderColor: "var(--border-1)" },
                  "&:hover fieldset": { borderColor: "var(--text-2)" },
                },
              }}
            />
          </Stack>

          {/* Tournament */}
          <Stack sx={{ flex: "1.5 1 0", minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                color: "var(--text-2)",
                mb: 0.5,
              }}
            >
              Tournament
            </Typography>
            <TextField
              size="small"
              placeholder="Tournament name"
              value={searchTournament}
              onChange={(e) => setSearchTournament(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                sx: {
                  borderRadius: "8px",
                  fontSize: "12px",
                  height: "38px",
                  backgroundColor: "var(--surface-2)",
                  "& fieldset": { borderColor: "var(--border-1)" },
                  "&:hover fieldset": { borderColor: "var(--text-2)" },
                },
              }}
            />
          </Stack>

          {/* Team */}
          <Stack sx={{ flex: "1.2 1 0", minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                color: "var(--text-2)",
                mb: 0.5,
              }}
            >
              Team
            </Typography>
            <TextField
              size="small"
              placeholder="Team name"
              value={searchTeam}
              onChange={(e) => setSearchTeam(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                sx: {
                  borderRadius: "8px",
                  fontSize: "12px",
                  height: "38px",
                  backgroundColor: "var(--surface-2)",
                  "& fieldset": { borderColor: "var(--border-1)" },
                  "&:hover fieldset": { borderColor: "var(--text-2)" },
                },
              }}
            />
          </Stack>

          {/* Status */}
          <Stack sx={{ flex: "1 1 0", minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                color: "var(--text-2)",
                mb: 0.5,
              }}
            >
              Status
            </Typography>
            <FilterSelectField
              options={analysisOptions}
              value={selectAnalyzedArray}
              onChange={setSelectAnalyzedArray}
              placeholder="Select status"
            />
          </Stack>

          {/* Start Date */}
          <Stack sx={{ flex: "0 0 auto" }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                color: "var(--text-2)",
                mb: 0.5,
              }}
            >
              Start Date
            </Typography>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              customInput={
                <CustomDateInput className="home-filter-date-input" />
              }
              popperProps={{ strategy: "fixed" }}
            />
          </Stack>

          {/* End Date */}
          <Stack sx={{ flex: "0 0 auto" }}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                color: "var(--text-2)",
                mb: 0.5,
              }}
            >
              End Date
            </Typography>
            <DatePicker
              customInput={
                <CustomDateInput className="home-filter-date-input" />
              }
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              popperProps={{ strategy: "fixed" }}
            />
          </Stack>

          {/* Buttons */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ flex: "0 0 auto", alignItems: "flex-end" }}
          >
            <Button
              onClick={handleReset}
              size="small"
              variant="outlined"
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                borderColor: "var(--border-1)",
                color: "var(--text-2)",
                px: 2,
                height: 38,
                fontSize: "13px",
                whiteSpace: "nowrap",
                "&:hover": {
                  borderColor: "var(--text-2)",
                  backgroundColor: "var(--surface-2)",
                },
              }}
              startIcon={<CloseOutlined sx={{ fontSize: "16px !important" }} />}
            >
              Reset
            </Button>
            <Button
              onClick={handleSearch}
              size="small"
              variant="contained"
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                px: 2.5,
                height: 38,
                fontSize: "13px",
                whiteSpace: "nowrap",
                backgroundColor: "rgba(232, 108, 0, 1)",
                "&:hover": { backgroundColor: "rgba(200, 93, 0, 1)" },
              }}
              startIcon={
                <SearchOutlined sx={{ fontSize: "16px !important" }} />
              }
            >
              Search
            </Button>
          </Stack>
        </Stack>
      </Collapse>

      {/* Loading Indicator */}
      {props.isLoading && (
        <LinearProgress
          sx={{
            borderRadius: "4px",
            height: 3,
            backgroundColor: "var(--surface-2)",
            "& .MuiLinearProgress-bar": {
              backgroundColor: "rgba(232, 108, 0, 1)",
            },
          }}
        />
      )}

      {/* Match Table */}
      <TableContainer
        className="home-match-table-container"
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid var(--border-1)",
          overflow: "auto",
          minHeight: "40vh",
          maxWidth: "100%",
          backgroundColor: "var(--surface-1)",
        }}
      >
        <Table className="home-match-table" stickyHeader size="small">
          <TableHead>
            <TableRow>
              {[
                { label: "ID", align: "left", sticky: true, width: "70px" },
                { label: "Date", align: "center", width: "100px" },
                { label: "Tournament", align: "left" },
                { label: "Team A", align: "left" },
                { label: "Team B", align: "left" },
                { label: "Status", align: "center", width: "140px" },
                { label: "TAT", align: "center", width: "90px" },
                { label: "Action", align: "center", width: "110px" },
                { label: "Details", align: "center", width: "70px" },
                { label: "Live", align: "center", width: "70px" },
                { label: "History", align: "center", width: "70px" },
              ].map((col, i) => (
                <TableCell
                  key={col.label}
                  align={col.align}
                  sx={{
                    fontSize: "12px",
                    fontWeight: "700",
                    fontFamily: "inherit",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: "var(--text-2) !important",
                    backgroundColor: "var(--surface-2) !important",
                    borderBottom: "1px solid var(--border-1) !important",
                    py: 1.5,
                    whiteSpace: "nowrap",
                    ...(col.sticky && {
                      position: "sticky",
                      left: 0,
                      zIndex: 999,
                      borderRight: "1px solid var(--border-1)",
                    }),
                    ...(col.width && { width: col.width, minWidth: col.width }),
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!props.isLoading && mainListData?.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} sx={{ border: 0 }}>
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{ py: 8, gap: 1 }}
                  >
                    <SportsSoccer
                      sx={{
                        fontSize: 48,
                        color: "var(--text-2)",
                        opacity: 0.3,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "15px",
                        fontWeight: 500,
                        color: "var(--text-2)",
                      }}
                    >
                      No matches found
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "var(--text-2)",
                        opacity: 0.7,
                      }}
                    >
                      Try adjusting your filters or check back later
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
            {mainListData?.map((item) => (
              <ItemCard
                key={item.id}
                isLoading={props.isLoading || props.isRefetching}
                isMobile={isMobile}
                match={item}
                hasSense
                onAnalysePress={() => dispatch(SetControllerToggle(true))}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={{
            py: 1,
            gap: 1,
          }}
        >
          <Pagination
            page={currentPage}
            count={totalPages}
            onChange={handlePageChange}
            shape="rounded"
            size={isMobile ? "small" : "medium"}
            siblingCount={isMobile ? 0 : 1}
            renderItem={(item) => (
              <PaginationItem
                slots={{ previous: ArrowBack, next: ArrowForward }}
                {...item}
                sx={{
                  borderRadius: "8px",
                  fontWeight: 500,
                  fontSize: "13px",
                  minWidth: 36,
                  height: 36,
                  color: "var(--text-1)",
                  border: "1px solid var(--border-1)",
                  backgroundColor: "var(--surface-1)",
                  "&.Mui-selected": {
                    backgroundColor: "rgba(232, 108, 0, 1) !important",
                    color: "#fff !important",
                    borderColor: "rgba(232, 108, 0, 1) !important",
                    fontWeight: 700,
                  },
                  "&:hover": {
                    backgroundColor: "var(--surface-2) !important",
                  },
                }}
              />
            )}
          />
        </Stack>
      )}
    </Stack>
  );
};

const ItemCard = (props) => {
  const { match, isMobile } = props;

  let history = useHistory();

  const { analystName, token } = useSelector((state) => ({
    token: state.auth.token,
    analystName: state.auth.analystName,
  }));

  const isAnalyst = match.analyst_details?.some(
    (analyst) => analyst.name === analystName,
  );
  const isReviewer = match.reviewer_details?.some(
    (reviewer) => reviewer.name === analystName,
  );

  let actionText = "";
  let actionBgColor = "";

  switch (match.status) {
    case 0:
    case 1:
      actionText = "Assign";
      actionBgColor = "rgba(232, 108, 0, 0.5)";
      break;
    case 2:
      actionText = "Assign";
      actionBgColor = "rgba(232, 108, 0, 1)";
      break;
    case 3:
      actionText = isReviewer ? "Review" : "Reanalyse";
      actionBgColor = "rgba(25, 171, 79, 1)";
      break;
    case 4:
      actionText = "Analyse";
      actionBgColor = "rgba(25, 171, 79, 1)";
      break;
    case 5:
      actionText = isReviewer
        ? isReviewer && isAnalyst
          ? "Analyze"
          : "Review"
        : "Analyse";
      actionBgColor = "rgba(25, 171, 79, 1)";
      break;
    case 6:
      actionText = "In Progress";
      actionBgColor = "rgba(232, 108, 0, 1)";
      break;
    case 7:
      actionText = isReviewer
        ? isAnalyst && isReviewer
          ? "Reanalyze"
          : "Review"
        : "Reanalyze";
      actionBgColor = "rgba(25, 171, 79, 1)";
      break;
    case 8:
      actionText = "Review";
      actionBgColor = "rgba(25, 171, 79, 1)";
      break;
    case 9:
      actionText = "In Progress";
      actionBgColor = "rgba(232, 108, 0, 1)";
      break;
    default:
      actionText = "Action";
      actionBgColor = "#6B7280";
  }

  const [isModalOpen, setModalOpen] = useState(false);

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);

  const { isUpdateLoading, mutateUpdateMatchStatus } = useUpdateMatchStatus({
    onClose: handleClose,
  });

  const handleAnalysePress = () => {
    if (
      (match.status === 6 ||
        match.status === 8 ||
        match.status === 3 ||
        match.status === 9) &&
      isReviewer
    ) {
      localStorage.setItem("videoTabTat", JSON.stringify({ [match?.id]: 0 }));
      trackEvent().track("clicked_on_review", {
        match_id: match.id,
        match_status: match.status,
      });
      history.push(`/review/${match.id}`);
      return;
    }
    handleReanalyze();
  };

  const handleReanalyze = () => {
    localStorage.setItem("videoTabTat", JSON.stringify({ [match?.id]: 0 }));
    const eventName =
      match.status === 3 || match.status === 7
        ? "clicked_on_reanalyze"
        : "clicked_on_analyze";
    trackEvent().track(eventName, {
      match_id: match.id,
      match_status: match.status,
    });
    isMobile
      ? history.push(`/analyse/${match.id}`)
      : match?.analysis_setup === 3
        ? history.push(`/videoplayer?match_id=${match.id}`)
        : history.push(`/videoplayerold?match_id=${match.id}`);
  };

  const handleReview = () => {
    trackEvent().track("clicked_on_mark_for_review", {
      match_id: match.id,
      match_status: match.status,
    });
    mutateUpdateMatchStatus({ token, match_id: match.id, match_status: 8 });
  };

  const ActionButton = () => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [confirmConfig, setConfirmConfig] = React.useState({
      open: false,
      title: "",
      message: "",
      onConfirm: () => {},
    });
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    const isAnalysisCompleted = match.status === 3;
    const isMarkForReview = match.status === 8;
    const isReviewInProgress = match.status === 6;
    const isIssueFound = match.status === 9;

    const buttonSx = {
      width: "120px",
      minWidth: "120px",
      borderRadius: "8px",
      textTransform: "none",
      fontWeight: 600,
      fontSize: isMobile ? "11px" : "13px",
      py: 0.75,
      transition: "all 0.15s",
      justifyContent: "center",
    };

    let buttonContent;

    switch (true) {
      case !isAnalysisCompleted && !isIssueFound && !isReviewer && isAnalyst:
        buttonContent = (
          <Button
            disabled={props.isLoading}
            onClick={() => {
              if (match.status === 6 || match.status === 8) return;
              handleAnalysePress();
            }}
            startIcon={
              <PlayArrowRounded sx={{ fontSize: "16px !important" }} />
            }
            sx={{
              ...buttonSx,
              backgroundColor: actionBgColor,
              color: "#fff",
              "&:hover": {
                backgroundColor: actionBgColor,
                filter: "brightness(0.9)",
              },
              opacity: match.status === 6 || match.status === 8 ? 0.4 : 1,
              pointerEvents:
                match.status === 6 || match.status === 8 ? "none" : "auto",
            }}
            variant="contained"
          >
            {props.isLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : match.status === 6 || match.status === 8 ? (
              "Waiting"
            ) : (
              actionText
            )}
          </Button>
        );
        break;

      case !isAnalysisCompleted &&
        isReviewer &&
        !isMarkForReview &&
        !isIssueFound &&
        !isReviewInProgress:
        buttonContent = (
          <Button
            disabled={props.isLoading}
            onClick={() => {
              if (isReviewer && isAnalyst) {
                handleAnalysePress();
              }
            }}
            startIcon={
              <PlayArrowRounded sx={{ fontSize: "16px !important" }} />
            }
            sx={{
              ...buttonSx,
              backgroundColor: actionBgColor,
              color: "#fff",
              "&:hover": {
                backgroundColor: actionBgColor,
                filter: "brightness(0.9)",
              },
              opacity: isReviewer && isAnalyst ? 1 : 0.4,
              pointerEvents: isReviewer && isAnalyst ? "auto" : "none",
            }}
            variant="contained"
          >
            {props.isLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              actionText
            )}
          </Button>
        );
        break;

      case (isReviewer &&
        (isMarkForReview || isIssueFound || isReviewInProgress) &&
        !isAnalyst) ||
        (isReviewer && isAnalyst && isMarkForReview) ||
        (isReviewer && isAnalyst && isReviewInProgress):
        buttonContent = (
          <Button
            disabled={props.isLoading}
            onClick={() => {
              if (
                (isReviewer && isAnalyst) ||
                (isReviewer && (isMarkForReview || isReviewInProgress))
              ) {
                handleAnalysePress();
              }
            }}
            variant="contained"
            startIcon={
              <PlayArrowRounded sx={{ fontSize: "16px !important" }} />
            }
            sx={{
              ...buttonSx,
              backgroundColor: "#8B5CF6",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#7C3AED",
              },
              opacity:
                (isReviewer && isAnalyst) ||
                (isReviewer && (isMarkForReview || isReviewInProgress))
                  ? 1
                  : 0.4,
              pointerEvents:
                (isReviewer && isAnalyst) ||
                (isReviewer && (isMarkForReview || isReviewInProgress))
                  ? "auto"
                  : "none",
            }}
          >
            {props.isLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              "Review"
            )}
          </Button>
        );
        break;

      case (isAnalysisCompleted && !isReviewer) ||
        (isIssueFound && !isReviewer) ||
        (isMarkForReview && !isReviewer) ||
        ((isAnalysisCompleted || isIssueFound) && isReviewer && isAnalyst):
        buttonContent = (
          <div style={{ display: "inline-flex", alignItems: "center" }}>
            <Button
              disabled={props.isLoading}
              id="menu-button"
              aria-controls={open ? "menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={handleClick}
              variant="contained"
              startIcon={
                <PlayArrowRounded sx={{ fontSize: "16px !important" }} />
              }
              endIcon={
                <KeyboardArrowDown sx={{ fontSize: "16px !important" }} />
              }
              sx={{
                ...buttonSx,
                backgroundColor: "rgba(25, 171, 79, 1)",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "rgba(20, 145, 65, 1)",
                },
                opacity: isMarkForReview && !isReviewer ? 0.4 : 1,
                pointerEvents: isMarkForReview && !isReviewer ? "none" : "auto",
              }}
            >
              {props.isLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                "Action"
              )}
            </Button>
            <Menu
              id="menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: {
                    borderRadius: "8px",
                    mt: 0.5,
                    minWidth: 160,
                    boxShadow: "var(--shadow-1)",
                    border: "1px solid var(--border-1)",
                  },
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  handleReanalyze();
                }}
                disabled={isUpdateLoading}
                sx={{ fontSize: "13px", py: 1 }}
              >
                Reanalyze
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  setConfirmConfig({
                    open: true,
                    title: "Mark for review",
                    message:
                      "Are you sure you want to mark this match for review?",
                    onConfirm: () => {
                      handleReview();
                      setConfirmConfig((prev) => ({ ...prev, open: false }));
                    },
                  });
                }}
                disabled={isUpdateLoading}
                sx={{ fontSize: "13px", py: 1 }}
              >
                {isUpdateLoading ? "Updating..." : "Mark for review"}
              </MenuItem>
            </Menu>
            <ActionConfirmationModal
              open={confirmConfig.open}
              onClose={() =>
                setConfirmConfig((prev) => ({ ...prev, open: false }))
              }
              onConfirm={confirmConfig.onConfirm}
              title={confirmConfig.title}
              message={confirmConfig.message}
              isProcessing={isUpdateLoading}
            />
          </div>
        );
        break;

      default:
        buttonContent = (
          <Button
            startIcon={
              <PlayArrowRounded sx={{ fontSize: "16px !important" }} />
            }
            sx={{
              ...buttonSx,
              backgroundColor: actionBgColor,
              color: "#fff",
              opacity: 0.4,
              pointerEvents: "none",
            }}
            variant="contained"
          >
            {actionText}
          </Button>
        );
    }
    return <>{buttonContent}</>;
  };

  const cellSx = {
    fontSize: { lg: "13px", md: "12px", xs: "11px" },
    color: "var(--text-1)",
    fontWeight: 400,
    fontFamily: "inherit",
    py: 1.5,
    borderBottom: "1px solid var(--border-1)",
  };

  return (
    <>
      <UpdateReviewStatusModal
        open={isModalOpen}
        onClose={handleClose}
        isProgress={match.status === 6}
        isCompleted={match.status === 7}
        issueFound={match.status === 9}
        matchId={match.id}
      />
      <TableRow
        hover
        sx={{
          cursor: "default",
          transition: "background-color 0.1s",
          "&:hover .MuiTableCell-root": {
            backgroundColor: "var(--surface-2) !important",
          },
          "&:last-child td": { border: 0 },
        }}
      >
        <TableCell
          sx={{
            ...cellSx,
            position: "sticky",
            left: 0,
            zIndex: 888,
            fontWeight: 700,
            backgroundColor: "var(--surface-1)",
            borderRight: "1px solid var(--border-1)",
            width: "70px",
            minWidth: "70px",
          }}
        >
          #{match?.id}
        </TableCell>
        <TableCell align="center" sx={cellSx}>
          {moment(match.created_at).format("DD MMM YY")}
        </TableCell>
        <TableCell sx={{ ...cellSx, maxWidth: "200px" }}>
          <Typography
            sx={{
              fontSize: "inherit",
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {match.tournament_name}
          </Typography>
        </TableCell>
        <TableCell sx={cellSx}>{match.team_a_details.name}</TableCell>
        <TableCell sx={cellSx}>{match.team_b_details.name}</TableCell>
        <TableCell align="center" sx={cellSx}>
          <StatusChip status={match.status} />
        </TableCell>
        <TableCell
          align="center"
          sx={{ ...cellSx, fontVariantNumeric: "tabular-nums" }}
        >
          {formatTimeFromSeconds(match.analyst_tat)}
        </TableCell>
        <TableCell align="center" sx={{ ...cellSx, px: 0.5 }}>
          <ActionButton />
        </TableCell>
        <TableCell align="center" sx={{ ...cellSx, px: 0.5 }}>
          <Button
            onClick={() => history.push(`/match-details/${match.id}`)}
            variant="outlined"
            size="small"
            startIcon={<InfoOutlined sx={{ fontSize: "15px !important" }} />}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontSize: isMobile ? "10px" : "12px",
              fontWeight: 600,
              py: 0.5,
              px: 1,
              borderColor: "var(--border-1)",
              color: "var(--text-1)",
              "&:hover": {
                borderColor: "var(--text-2)",
                backgroundColor: "var(--surface-2)",
              },
            }}
          >
            Details
          </Button>
        </TableCell>
        <TableCell align="center" sx={{ ...cellSx, px: 0.5 }}>
          <Button
            disabled={
              match.status === 3 || 
              match.status === 6 ||
              match.status === 8 ||
              match.status === 9 ||
              match.status === 7 ||
              match.live_tagging_status === null
            }
            onClick={() =>
              history.push({
                pathname: `/live-analyse/${match.id}`,
                state: {
                  matchStatus: match.status,
                  liveTaggingStatus: match.live_tagging_status,
                },
              })
            }
            variant="outlined"
            size="small"
            startIcon={
              <CellTowerRounded sx={{ fontSize: "15px !important" }} />
            }
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontSize: isMobile ? "10px" : "12px",
              fontWeight: 600,
              py: 0.5,
              px: 1,
              borderColor: "#3B82F6",
              color: "#3B82F6",
              "&:hover": {
                borderColor: "#2563EB",
                backgroundColor: "rgba(59, 130, 246, 0.08)",
              },
              "&.Mui-disabled": {
                borderColor: "var(--border-1)",
                color: "var(--text-2)",
                opacity: 0.4,
              },
            }}
          >
            Live Tag
          </Button>
        </TableCell>

        {/* ── Version History button ─────────────────────────────────────── */}
        <TableCell align="center">
          <Tooltip title="View version history / checkpoints">
            <IconButton
              size="small"
              onClick={() => history.push(`/history/${match.id}`)}
              sx={{
                color: "var(--text-2, #64748b)",
                border: "1px solid var(--border-1, #e2e8f0)",
                borderRadius: "6px",
                padding: "5px 8px",
                gap: "4px",
                "&:hover": {
                  backgroundColor: "#eff6ff",
                  borderColor: "#3b82f6",
                  color: "#3b82f6",
                },
              }}
            >
              <History sx={{ fontSize: isMobile ? 14 : 16 }} />
              {!isMobile && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: 11,
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    lineHeight: 1,
                  }}
                >
                  History
                </Typography>
              )}
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    </>
  );
};

const ListHeader = ({ isMobile }) => {
  return (
    <TableHead sx={{ backgroundColor: "var(--surface-2)" }}>
      <TableRow
        sx={{
          "&:last-child td, &:last-child th": { border: 0 },
        }}
      >
        <TableCell
          sx={{
            position: "sticky",
            left: 0,
            zIndex: 999,
            fontSize: { lg: "14px", md: "12px", xs: "10px" },
            fontWeight: "600",
            fontFamily: "Poppins",
            color: "var(--text-1)",
            lineHeight: "120%",
            backgroundColor: "var(--surface-2)",
            minWidth: "80px",
            borderRight: "1px solid var(--border-1)",
          }}
          component="th"
          scope="row"
        >
          Match ID
        </TableCell>
        <TableCell
          sx={{
            fontSize: { lg: "14px", md: "12px", xs: "10px" },
            fontWeight: "500",
            fontFamily: "Poppins",
            color: "var(--text-1)",
            lineHeight: "120%",
            backgroundColor: "var(--surface-2)",
          }}
          align="center"
        >
          Created Date
        </TableCell>
        <TableCell
          sx={{
            fontSize: { lg: "14px", md: "12px", xs: "10px" },
            color: "var(--text-1)",
            fontWeight: "500",
            fontFamily: "Poppins",
            lineHeight: "120%",
            backgroundColor: "var(--surface-2)",
          }}
          align="center"
        >
          Tournament Name
        </TableCell>
        <TableCell
          align="right"
          sx={{
            fontSize: { lg: "14px", md: "12px", xs: "10px" },
            color: "var(--text-1)",
            fontWeight: "500",
            fontFamily: "Poppins",
            backgroundColor: "var(--surface-2)",
          }}
        >
          Team A
        </TableCell>
        <TableCell
          sx={{
            fontSize: { lg: "14px", md: "12px", xs: "10px" },
            color: "var(--text-1)",
            fontWeight: "500",
            fontFamily: "Poppins",
            backgroundColor: "var(--surface-2)",
          }}
          align="left"
        >
          Team B
        </TableCell>
        <TableCell
          sx={{
            fontSize: { lg: "14px", md: "12px", xs: "10px" },
            fontWeight: "500",
            fontFamily: "Poppins",
            color: "var(--text-1)",
            backgroundColor: "var(--surface-2)",
          }}
          align="left"
        >
          Status
        </TableCell>
        <TableCell
          sx={{
            fontSize: { lg: "14px", md: "12px", xs: "10px" },
            fontWeight: "500",
            fontFamily: "Poppins",
            color: "var(--text-1)",
            backgroundColor: "var(--surface-2)",
          }}
          align="center"
        >
          TAT
        </TableCell>
        <TableCell
          sx={{
            fontSize: { lg: "14px", md: "12px", xs: "10px" },
            fontWeight: "500",
            fontFamily: "Poppins",
            color: "var(--text-1)",
            backgroundColor: "var(--surface-2)",
          }}
          align="center"
        >
          Action
        </TableCell>
        <TableCell
          sx={{
            fontSize: { lg: "14px", md: "12px", xs: "10px" },
            color: "var(--text-1)",
            fontWeight: "500",
            fontFamily: "Poppins",
            backgroundColor: "var(--surface-2)",
          }}
          align="center"
        >
          Action 2
        </TableCell>
        <TableCell
          sx={{
            fontSize: { lg: "14px", md: "12px", xs: "10px" },
            color: "var(--text-1)",
            fontWeight: "500",
            fontFamily: "Poppins",
            backgroundColor: "var(--surface-2)",
          }}
          align="center"
        >
          History
        </TableCell>
      </TableRow>
    </TableHead>
  );
};

export default Home;
